import {
  ArrowRightIcon,
  CopyIcon,
  IssueOpenedIcon,
  KebabHorizontalIcon,
  LockIcon,
  TrashIcon,
  XIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, Box, IconButton} from '@primer/react'
import {useMemo, useRef, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import {BUTTON_LABELS, LABELS} from '../../constants'
import type {HeaderMenu$key} from './__generated__/HeaderMenu.graphql'
import {HeaderNavigation} from './HeaderNavigation'
import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard'
import {CreateIssueButton} from '@github-ui/issue-create'
import type {MarkdownEditorConfig} from '@github-ui/markdown-editor'
import {IssueTransferDialog} from './IssueTransferDialog'
import {IssueConversationLock} from './IssueConversationLock'
import {IssueTypesDialog} from './IssueTypesDialog'
import {NotificationToggleButton} from './NotificationToggleButton'
import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {IssueDeletionConfirmationDialog} from './IssueDeletionConfirmationDialog'

type ScopedRepository = {
  id: string
  name: string
  owner: string
}

const HeaderMenuFragment = graphql`
  fragment HeaderMenu on Issue {
    id
    isTransferInProgress
    locked
    viewerCanUpdateNext
    viewerCanDelete
    viewerCanTransfer
    viewerThreadSubscriptionFormAction
    repository {
      isPrivate
      issueTypes(first: 10) {
        ...IssueTypesDialogIssueTypes
      }
    }
    issueType {
      ...IssueTypesDialogIssueType
    }
    url
  }
`

export type HeaderMenuProps = {
  menuData: HeaderMenu$key
} & HeaderMenuSharedProps

export type HeaderMenuSharedProps = {
  customEditMenuEntries?: JSX.Element[]
  navigatePrevious?: () => void
  navigateNext?: () => void
  navigateBack?: () => void
  navigate(url: string): void
  showCreateIssueButton?: boolean
  onIssueDelete?: () => void
  onClose?: () => void
  additionalActions?: React.ReactNode
  scopedRepository?: ScopedRepository
  markdownEditorConfig?: MarkdownEditorConfig
  singleKeyShortcutsEnabled: boolean
  metadataPaneTrigger?: JSX.Element
  prefetchInteractionDataEnabled: boolean
  collapsed?: boolean
}

export function HeaderMenu({
  menuData,
  customEditMenuEntries,
  navigatePrevious,
  navigateNext,
  navigateBack,
  navigate,
  showCreateIssueButton,
  additionalActions,
  onIssueDelete,
  onClose,
  markdownEditorConfig,
  singleKeyShortcutsEnabled,
  scopedRepository,
  prefetchInteractionDataEnabled,
  collapsed,
}: HeaderMenuProps) {
  const [showIssueTransferDialog, setShowIssueTransferDialog] = useState(false)
  const [showIssueDeletionConfirmationDialog, setShowIssueDeletionConfirmationDialog] = useState(false)
  const [showConversationLockDialog, setShowConversationLockDialog] = useState(false)
  const [showIssueTypesDialog, setShowIssueTypesDialog] = useState(false)

  const {
    id,
    locked,
    viewerCanDelete,
    viewerCanUpdateNext,
    viewerCanTransfer,
    viewerThreadSubscriptionFormAction,
    isTransferInProgress,
    url,
    repository: {isPrivate, issueTypes},
    issueType,
  } = useFragment(HeaderMenuFragment, menuData)

  const viewerSubscribed = viewerThreadSubscriptionFormAction === 'UNSUBSCRIBE'

  const anchorRef = useRef<HTMLButtonElement>(null)

  const analyticsMetadata = useMemo(
    () => ({
      owner: scopedRepository?.owner ?? '',
      repositoryName: scopedRepository?.name ?? '',
      issueId: id,
    }),
    [id, scopedRepository?.name, scopedRepository?.owner],
  )

  const showMenuButton = viewerCanUpdateNext || viewerCanDelete || viewerCanTransfer

  return (
    <>
      <Box sx={{display: 'flex', gap: 1}}>
        {showCreateIssueButton && (
          <CreateIssueButton
            label={BUTTON_LABELS.new}
            navigate={navigate}
            optionConfig={{
              scopedRepository,
              showFullScreenButton: true,
              singleKeyShortcutsEnabled,
              useMonospaceFont: markdownEditorConfig?.useMonospaceFont,
              pasteUrlsAsPlainText: markdownEditorConfig?.pasteUrlsAsPlainText,
              lazyLoad: !prefetchInteractionDataEnabled,
            }}
          />
        )}
        <HeaderNavigation navigatePrevious={navigatePrevious} navigateNext={navigateNext} collapsed={collapsed} />

        <NotificationToggleButton id={id} subscribed={viewerSubscribed} />
        <CopyToClipboardButton
          textToCopy={url}
          ariaLabel={BUTTON_LABELS.copyIssueLink}
          icon={CopyIcon}
          tooltipProps={{direction: 's'}}
        />
        {additionalActions}
        <ActionMenu anchorRef={anchorRef}>
          {showMenuButton ? (
            <ActionMenu.Anchor>
              <IconButton
                variant="invisible"
                sx={{flexShrink: 0}}
                icon={KebabHorizontalIcon}
                aria-label={BUTTON_LABELS.editIssue}
                ref={anchorRef}
              />
            </ActionMenu.Anchor>
          ) : (
            <></>
          )}
          <ActionMenu.Overlay>
            <ActionList>
              {customEditMenuEntries && (
                <>
                  {customEditMenuEntries.map(e => e)}
                  <ActionList.Divider />
                </>
              )}
              {viewerCanUpdateNext && issueTypes && (
                <ActionList.Item onSelect={() => setShowIssueTypesDialog(true)}>
                  <ActionList.LeadingVisual>
                    <IssueOpenedIcon />
                  </ActionList.LeadingVisual>
                  {BUTTON_LABELS.changeIssueType}
                </ActionList.Item>
              )}
              {viewerCanTransfer && (
                <ActionList.Item onSelect={() => setShowIssueTransferDialog(true)}>
                  <ActionList.LeadingVisual>
                    <ArrowRightIcon />
                  </ActionList.LeadingVisual>
                  {BUTTON_LABELS.transferIssue}
                </ActionList.Item>
              )}
              {viewerCanUpdateNext && (
                <ActionList.Item onSelect={() => setShowConversationLockDialog(true)}>
                  <ActionList.LeadingVisual>
                    <LockIcon />
                  </ActionList.LeadingVisual>
                  {locked ? LABELS.lock.buttonConfirmUnlock : LABELS.lock.buttonConfirmLock}
                </ActionList.Item>
              )}
              {viewerCanDelete && (
                <ActionList.Item variant="danger" onSelect={() => setShowIssueDeletionConfirmationDialog(true)}>
                  <ActionList.LeadingVisual>
                    <TrashIcon />
                  </ActionList.LeadingVisual>
                  {BUTTON_LABELS.deleteIssue}
                </ActionList.Item>
              )}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
        {showIssueTransferDialog && scopedRepository && (
          <IssueTransferDialog
            owner={scopedRepository.owner}
            repository={scopedRepository.name}
            isRepoPrivate={isPrivate}
            issueId={id}
            isTransferInProgress={isTransferInProgress}
            onClose={() => setShowIssueTransferDialog(false)}
          />
        )}
        {viewerCanUpdateNext && issueTypes && (
          <AnalyticsProvider appName="issue_types" category="issue_viewer" metadata={analyticsMetadata}>
            <IssueTypesDialog
              onClose={() => setShowIssueTypesDialog(false)}
              issueId={id}
              issueTypes={issueTypes}
              initialSelectedType={issueType}
              isOpen={showIssueTypesDialog}
              returnFocusRef={anchorRef}
            />
          </AnalyticsProvider>
        )}
        {showConversationLockDialog && (
          <IssueConversationLock
            issueId={id}
            isUnlocked={!locked}
            onClose={() => setShowConversationLockDialog(false)}
          />
        )}
        {onClose && (
          <IconButton variant="invisible" aria-label={BUTTON_LABELS.closePanel} icon={XIcon} onClick={onClose} />
        )}
      </Box>
      {showIssueDeletionConfirmationDialog && (
        <IssueDeletionConfirmationDialog
          issueId={id}
          onSuccessfulDeletion={onIssueDelete}
          afterDelete={navigateBack}
          onClose={() => setShowIssueDeletionConfirmationDialog(false)}
        />
      )}
    </>
  )
}

try{ HeaderMenu.displayName ||= 'HeaderMenu' } catch {}